import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import Variables from '../../design-system/variables';
import AddIcon from '@mui/icons-material/Add';
import OvBackButton from '../UI/atoms/OvBackButton';
import OvRecordDetailsErrorState from '../UI/molecules/OvRecordDetailsErrorState';
import OvRecordDetailsSection from '../UI/molecules/OvRecordDetailsSection';
import OvRecordHeaderActions from '../UI/molecules/OvRecordHeaderActions';
import OvRecordDetailsHeader from '../UI/organisms/OvRecordDetailsHeader';
import OvCompactButton from '../UI/atoms/OvCompactButton';
import { Article } from '../../common/model/dto/article';
import { useAdminArticle } from '../../hooks/use-admin-article';
import { useAdminArticleMutations } from '../../hooks/use-admin-article-mutations';
import { getAxiosErrorMessage } from '../../common/utils/helpers';
import OvErrorMessage from '../UI/atoms/OvErrorMessage';
import OvLoadingIndicator from '../UI/atoms/OvLoadingIndicator';
import OvRecordDetailsCanvas from '../UI/atoms/OvRecordDetailsCanvas';
import OvRecordDetailsPage from '../UI/atoms/OvRecordDetailsPage';
import * as Yup from 'yup';
import { Field, Form, Formik } from 'formik';
import OvTextFieldFormControl from '../UI/atoms/OvTextFieldFormControl';
import Colours from '../../design-system/colours';
import { OOVA_ARTICLE_ASSETS } from '../../api/firebase-storage';
import OvImageUpload from '../UI/molecules/OvImageUpload';
import OvFormObserver from '../UI/atoms/OvFormObserver';

interface ArticleFormProps {}

const ARTICLE_LIST_PATH = '/articles';

const ArticleForm: React.FC<ArticleFormProps> = () => {
  let { articleId }: any = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    data: article,
    isFetching: isArticleFetching,
    error: articleError,
    refetch,
  } = useAdminArticle(articleId);
  const { createArticle, updateArticle } = useAdminArticleMutations();
  const isLoading =
    isArticleFetching || createArticle.isPending || updateArticle.isPending;
  const error = articleError ?? createArticle.error ?? updateArticle.error;
  const errorMessage = error ? getAxiosErrorMessage(error) : '';

  const [imageUrl, setImageUrl] = useState<string>();
  const [articleFormValue, setArticleFormValue] = useState<Article>();

  useEffect(() => {
    if (article) {
      initForm(article);
    }

    // eslint-disable-next-line
  }, [article]);

  const initForm = (article: Article) => {
    setImageUrl(article.image_url);
    setArticleFormValue(article);
  };

  const handleSuccessfulImageUpload = ({
    image_path,
  }: {
    image_path: string;
  }) => {
    setImageUrl(image_path);
  };

  const saveArticle = () => {
    if (!articleFormValue) {
      return;
    }

    const articleDTO: Article = {
      ...article,
      ...articleFormValue,
      image_url: imageUrl,
    };

    const mutation = articleDTO.id ? updateArticle : createArticle;

    mutation.mutate(articleDTO, {
      onSuccess: ({ id }) => navigate(`/articles/detail/${id}`),
    });
  };

  const handleFormValueChange = (value: Article) => {
    setArticleFormValue(value);
  };

  const pageTitle = t(articleId ? 'articles.update' : 'articles.create');

  if (articleId && !article) {
    return (
      <OvRecordDetailsPage>
        {articleError ? (
          <OvRecordDetailsErrorState
            title={t('articles.details.errorTitle')}
            error={articleError}
            onRetry={() => refetch()}
            backLabel={t('common.articles')}
            backPath={ARTICLE_LIST_PATH}
          />
        ) : (
          <OvLoadingIndicator position="fixed" />
        )}
      </OvRecordDetailsPage>
    );
  }

  return (
    <OvRecordDetailsPage aria-busy={isLoading}>
      {errorMessage && <OvErrorMessage message={errorMessage} />}

      <OvRecordDetailsCanvas>
        <OvRecordDetailsHeader
          breadcrumbLabel={t('articles.details.breadcrumb')}
          backControl={<OvBackButton label={t('common.articles')} />}
          current={article?.title ?? pageTitle}
          title={pageTitle}
          meta={article?.topic}
          actions={
            <OvRecordHeaderActions>
              <OvCompactButton onClick={saveArticle} icon={<AddIcon />}>
                {pageTitle}
              </OvCompactButton>
            </OvRecordHeaderActions>
          }
        />

        <OvRecordDetailsSection title={t('articles.details.section')}>
          <FormWrapper>
            <ImageUploadContainer>
              <OvImageUpload
                imageBasePath={OOVA_ARTICLE_ASSETS}
                imageUrl={article?.image_url}
                title={t('articles.images.thumbnail')}
                hintText={t('articles.images.thumbnailHint')}
                ctaLabel={t('imageUpload.uploadImage')}
                dialogTitle={t('articles.images.thumbnailUploadDialogTitle')}
                handleChange={handleSuccessfulImageUpload}
              />
            </ImageUploadContainer>

            <Formik
              initialValues={{
                title: article?.title || '',
                topic: article?.topic || '',
                section: article?.section || '',
                timing: article?.timing || 0,
                word_count: article?.word_count || 0,
                days_in_post_ovulation: article?.days_in_post_ovulation || 0,
                cta: article?.cta || '',
                color: article?.color || '',
                url: article?.url || '',
              }}
              enableReinitialize={true}
              onSubmit={(values) => console.log(values)}
              validationSchema={Yup.object().shape({
                title: Yup.string().required(t('common.requiredField')),
                topic: Yup.string().required(t('common.requiredField')),
                section: Yup.string().required(t('common.requiredField')),
                timing: Yup.number().required(t('common.requiredField')),
                word_count: Yup.number().required(t('common.requiredField')),
                days_in_post_ovulation: Yup.number().required(
                  t('common.requiredField')
                ),
                cta: Yup.string().required(t('common.requiredField')),
                color: Yup.string().required(t('common.requiredField')),
                url: Yup.string()
                  .url(t('common.invalidUrl'))
                  .required(t('common.requiredField')),
              })}
              validateOnBlur={true}
            >
              <StyledForm>
                <StyledFormLabel>
                  <StyledLabelText>
                    {t('articles.fields.title')}
                  </StyledLabelText>

                  <Field
                    component={StyledInputField}
                    type="text"
                    name="title"
                    placeholder={t('articles.fields.title')}
                  />
                </StyledFormLabel>

                <StyledFormLabel>
                  <StyledLabelText>
                    {t('articles.fields.topic')}
                  </StyledLabelText>

                  <Field
                    component={StyledInputField}
                    type="text"
                    name="topic"
                    placeholder={t('articles.fields.topic')}
                  />
                </StyledFormLabel>

                <StyledFormLabel>
                  <StyledLabelText>
                    {t('articles.fields.section')}
                  </StyledLabelText>

                  <Field
                    component={StyledInputField}
                    type="text"
                    name="section"
                    placeholder={t('articles.fields.section')}
                  />
                </StyledFormLabel>

                <StyledFormLabel>
                  <StyledLabelText>
                    {t('articles.fields.timing')}
                  </StyledLabelText>
                  <Field
                    component={StyledInputField}
                    type="number"
                    name="timing"
                    placeholder={t('articles.fields.timing')}
                  />
                </StyledFormLabel>

                <StyledFormLabel>
                  <StyledLabelText>
                    {t('articles.fields.wordCount')}
                  </StyledLabelText>
                  <Field
                    component={StyledInputField}
                    type="number"
                    name="word_count"
                    placeholder={t('articles.fields.wordCount')}
                  />
                </StyledFormLabel>

                <StyledFormLabel>
                  <StyledLabelText>
                    {t('articles.fields.daysInPostOvulation')}
                  </StyledLabelText>
                  <Field
                    component={StyledInputField}
                    type="number"
                    name="days_in_post_ovulation"
                    placeholder={t('articles.fields.daysInPostOvulation')}
                  />
                </StyledFormLabel>

                <StyledFormLabel>
                  <StyledLabelText>{t('articles.fields.cta')}</StyledLabelText>
                  <Field
                    component={StyledInputField}
                    type="text"
                    name="cta"
                    placeholder={t('articles.fields.cta')}
                  />
                </StyledFormLabel>

                <StyledFormLabel>
                  <StyledLabelText>
                    {t('articles.fields.color')}
                  </StyledLabelText>
                  <Field
                    component={StyledInputField}
                    type="text"
                    name="color"
                    placeholder={t('articles.fields.color')}
                  />
                </StyledFormLabel>

                <StyledFormLabel>
                  <StyledLabelText>{t('articles.fields.url')}</StyledLabelText>
                  <Field
                    component={StyledInputField}
                    type="text"
                    name="url"
                    placeholder={t('articles.fields.url')}
                  />
                </StyledFormLabel>

                <OvFormObserver valuesChanged={handleFormValueChange} />
              </StyledForm>
            </Formik>
          </FormWrapper>
        </OvRecordDetailsSection>
      </OvRecordDetailsCanvas>

      {isLoading && <OvLoadingIndicator position="fixed" />}
    </OvRecordDetailsPage>
  );
};

export default ArticleForm;

const ImageUploadContainer = styled.div`
  margin-bottom: 0.75rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const FormWrapper = styled.div`
  max-width: 100%;
`;

const StyledForm = styled(Form)`
  && {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
  }
`;

const StyledFormLabel = styled.label`
  display: block;
  flex-basis: calc(50% - 0.5rem);

  &:last-child {
    margin-bottom: 0;
  }
`;

const StyledLabelText = styled.p`
  font-size: ${Variables.fontSizes.MEDIUM};
  font-weight: bold;
  text-indent: 0.5rem;
  margin-bottom: 0.5rem;
  margin-top: 0;
`;

const StyledInputField = styled(OvTextFieldFormControl)`
  && {
    .MuiFormHelperText-root.Mui-error {
      background: ${Colours.OV_WHITE};
      margin: 0;
      padding: 0.25rem 0.75rem;
    }

    &.MuiFormControl-root {
      width: 100%;

      .MuiInputBase-root {
        width: 100%;
      }
    }
  }
`;
